import { useEffect, useId, useState } from 'react'
import { Autocomplete } from '@base-ui/react/autocomplete'
import { zodResolver } from '@hookform/resolvers/zod'
import { ShieldCheck, Users } from 'lucide-react'
import { useForm, useWatch } from 'react-hook-form'
import { z } from 'zod'

import { cn } from '@/lib/utils'
import { useAnnounce } from '@/shared/providers/announcer-provider'
import { Button } from '@/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogTitle } from '@/ui/dialog'
import { RolePicker } from '@/ui/role-picker'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/select'

import {
  useUserManagementEntity,
  useUserManagementEntityIds,
} from '@/modules/iam/user-management-entity-queries'
import { useAddGroupMember, useEntityGroups } from '@/modules/iam/features/groups/group-queries'
import {
  EntitySearchInput,
  EntitySearchResults,
  searchStatusText,
  useEntitySearchBox,
} from '@/modules/hierarchy'
import { useAssignableRoles } from '@/modules/iam/features/roles/role-queries'
import type { UserCandidate } from '@/modules/iam/api/users-api'
import {
  assignUserRoleErrorMessage,
  useAssignUserRole,
  useUserAccessProfile,
} from './user-queries'

const grantAccessSchema = z
  .object({
    accessType: z.enum(['role', 'group']),
    entityId: z.string().min(1),
    roleId: z.string(),
    groupId: z.string(),
    userId: z.string().min(1),
  })
  .superRefine((value, context) => {
    if (value.accessType === 'role' && !value.roleId)
      context.addIssue({ code: 'custom', path: ['roleId'], message: 'Select a role.' })
    if (value.accessType === 'group' && !value.groupId)
      context.addIssue({ code: 'custom', path: ['groupId'], message: 'Select a group.' })
  })

type GrantAccessValues = z.infer<typeof grantAccessSchema>
type Step = 'type' | 'details' | 'review'

const steps: { id: Step; label: string }[] = [
  { id: 'type', label: 'Access type' },
  { id: 'details', label: 'Details' },
  { id: 'review', label: 'Review' },
]

function StepRail({ current }: { current: Step }) {
  const currentIndex = steps.findIndex((step) => step.id === current)
  return (
    <ol className="grid grid-cols-3 gap-2" aria-label="Grant access progress">
      {steps.map((step, index) => (
        <li
          key={step.id}
          aria-current={step.id === current ? 'step' : undefined}
          className={cn(
            'border-border flex items-center gap-2 border-t-2 pt-2 text-[11px]',
            index <= currentIndex ? 'border-primary text-foreground' : 'text-muted-foreground',
          )}
        >
          <span className="font-medium tabular-nums">{index + 1}</span>
          {step.label}
        </li>
      ))}
    </ol>
  )
}

function AccessTypeCard({
  value,
  checked,
  title,
  description,
  icon: Icon,
  onChange,
}: {
  value: 'role' | 'group'
  checked: boolean
  title: string
  description: string
  icon: typeof ShieldCheck
  onChange: (value: 'role' | 'group') => void
}) {
  return (
    <label
      className={cn(
        'flex min-h-28 cursor-pointer flex-col items-start rounded-md border p-3 outline-none transition-colors focus-within:ring-2 focus-within:ring-ring/30',
        checked
          ? 'border-primary/40 bg-primary/[0.07]'
          : 'border-border hover:bg-foreground/[0.04]',
      )}
    >
      <input
        className="sr-only"
        type="radio"
        name="access-type"
        value={value}
        checked={checked}
        onChange={() => onChange(value)}
      />
      <span className={cn('mb-3 flex size-8 items-center justify-center rounded-md', checked ? 'bg-primary/12 text-primary' : 'bg-muted text-muted-foreground')}>
        <Icon className="size-4" aria-hidden="true" />
      </span>
      <span className="text-sm font-medium">{title}</span>
      <span className="text-muted-foreground mt-1 text-[11px]/normal">{description}</span>
    </label>
  )
}

export function AssignAccessPanel({
  entityId,
  entityName = 'this entity',
  user,
  onClose,
  onPendingChange,
  onAssigned,
  rolesOnly = false,
  serviceAccount = false,
}: {
  entityId: string
  entityName?: string
  user: UserCandidate
  onClose: () => void
  onPendingChange?: (pending: boolean) => void
  onAssigned?: (message: string) => void
  rolesOnly?: boolean
  serviceAccount?: boolean
}) {
  const [step, setStep] = useState<Step>(rolesOnly ? 'details' : 'type')
  const [selectedEntity, setSelectedEntity] = useState({ entityId, entityName })
  const [editingEntity, setEditingEntity] = useState(false)
  const requestErrorId = useId()
  const { control, handleSubmit, setValue } = useForm<GrantAccessValues>({
    resolver: zodResolver(grantAccessSchema),
    defaultValues: { accessType: 'role', entityId, roleId: '', groupId: '', userId: user.userId },
  })
  const accessType = useWatch({ control, name: 'accessType' })
  const roleId = useWatch({ control, name: 'roleId' })
  const groupId = useWatch({ control, name: 'groupId' })
  const userId = useWatch({ control, name: 'userId' })

  const managementEntity = useUserManagementEntity(entityId)
  const managementEntityId =
    managementEntity.status === 'ready'
      ? (managementEntity.userManagementEntityId ?? entityId)
      : entityId

  const groupsQuery = useEntityGroups(rolesOnly ? null : managementEntityId)
  const rolesQuery = useAssignableRoles(selectedEntity.entityId, { enabled: accessType === 'role' })
  const profileQuery = useUserAccessProfile(
    managementEntityId,
    accessType === 'role' ? userId : null,
    serviceAccount,
  )
  const assignRole = useAssignUserRole(serviceAccount)
  const addMember = useAddGroupMember(managementEntityId, groupId || '')
  const pending = assignRole.isPending || addMember.isPending

  const { query, setQuery, isSearching, search, results, pending: searchPending } = useEntitySearchBox(managementEntityId)
  const managedRoots = useUserManagementEntityIds(results.map((result) => result.entityId))
  const subtreeResults = results.filter(
    (result) =>
      managedRoots.userManagementEntityIds.get(result.entityId) ===
      managementEntityId,
  )
  const searchError = search.error ?? (subtreeResults.length === 0 ? managedRoots.error : null)

  const selectedRole = rolesQuery.data?.find((role) => role.roleId === roleId) ?? null
  const editableGroups = (groupsQuery.data?.items ?? []).filter(
    (group) => group.active && !group.managedByScim,
  )
  const selectedGroup = editableGroups.find((group) => group.groupId === groupId) ?? null
  const alreadyHasRole = (profileQuery.data?.grants ?? []).some(
    (grant) => grant.entityId === selectedEntity.entityId && grant.roleId === roleId && grant.viaGroupId === null,
  )
  const canReview = accessType === 'group'
    ? selectedGroup !== null
    : selectedRole !== null && !alreadyHasRole

  useEffect(() => {
    onPendingChange?.(pending)
  }, [onPendingChange, pending])

  function chooseType(next: 'role' | 'group') {
    setValue('accessType', next)
    setValue('roleId', '')
    setValue('groupId', '')
    assignRole.reset()
    addMember.reset()
  }

  function submitGrant(values: GrantAccessValues) {
    if (values.accessType === 'role' && selectedRole) {
      assignRole.mutate(
        { entityId: values.entityId, userId: values.userId, roleId: values.roleId },
        { onSuccess: () => {
          onAssigned?.(`Granted ${selectedRole.name} to ${user.name}`)
          onClose()
        } },
      )
      return
    }
    if (values.accessType === 'group' && selectedGroup) {
      addMember.mutate(values.userId, { onSuccess: () => {
        onAssigned?.(`Added ${user.name} to ${selectedGroup.name}`)
        onClose()
      } })
    }
  }

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(event) => event.preventDefault()}
    >
      {!rolesOnly ? <StepRail current={step} /> : null}

      {step === 'type' && !rolesOnly ? (
        <fieldset className="grid grid-cols-2 gap-2">
          <legend className="mb-2 text-xs font-medium">What kind of access do you want to grant?</legend>
          <AccessTypeCard value="role" checked={accessType === 'role'} title="Role" description="Grant permissions at one entity and its descendants." icon={ShieldCheck} onChange={chooseType} />
          <AccessTypeCard value="group" checked={accessType === 'group'} title="Group" description="Add the user to a managed set of shared access." icon={Users} onChange={chooseType} />
        </fieldset>
      ) : null}

      {step === 'details' ? (
        <div className="flex flex-col gap-3">
          {accessType === 'role' ? (
            <>
              <div className="flex flex-col gap-1.5">
                <span className="text-xs font-medium">Entity</span>
                <Autocomplete.Root
                  mode="none"
                  open={editingEntity && isSearching && searchError == null}
                  onOpenChange={() => {}}
                  value={editingEntity ? query : selectedEntity.entityName}
                  onValueChange={(value) => { setEditingEntity(true); setQuery(value) }}
                >
                  <div className="relative">
                    <EntitySearchInput
                      query={editingEntity ? query : selectedEntity.entityName}
                      onValueChange={(value) => { setEditingEntity(true); setQuery(value) }}
                      onFocus={(event) => event.currentTarget.select()}
                      onKeyDown={(event) => {
                        if (event.key === 'Escape' && editingEntity) {
                          event.preventDefault(); setEditingEntity(false); setQuery('')
                        }
                      }}
                      label="Entity"
                      placeholder="Search managed entities…"
                      disabled={pending}
                    />
                    <Autocomplete.Status className="sr-only">
                      {searchStatusText(editingEntity && isSearching, searchPending || managedRoots.isPending, searchError != null, subtreeResults.length)}
                    </Autocomplete.Status>
                    {editingEntity && isSearching ? (
                      <div className="bg-popover border-border absolute top-full right-0 left-0 z-10 mt-1 rounded-md border p-1 shadow-md">
                        <EntitySearchResults
                          results={subtreeResults}
                          error={searchError}
                          hasNextPage={(search.data?.pageInfo.hasNextPage ?? false) && subtreeResults.length > 0}
                          selectedId={selectedEntity.entityId}
                          onSelect={(entity) => {
                            setSelectedEntity({ entityId: entity.id, entityName: entity.name })
                            setValue('entityId', entity.id); setValue('roleId', '')
                            setEditingEntity(false); setQuery('')
                          }}
                          onRetry={() => search.error ? void search.refetch() : managedRoots.retry()}
                        />
                      </div>
                    ) : null}
                  </div>
                </Autocomplete.Root>
              </div>
              <div className="flex flex-col gap-1.5">
                <span className="text-xs font-medium">Role</span>
                <RolePicker
                  aria-label="Role"
                  roles={rolesQuery.data ?? []}
                  isLoading={rolesQuery.isLoading}
                  error={rolesQuery.error}
                  onRetry={() => void rolesQuery.refetch()}
                  value={roleId || null}
                  onChange={(next) => setValue('roleId', next)}
                  disabled={pending}
                />
              </div>
            </>
          ) : (
            <div className="flex flex-col gap-1.5">
              <span className="text-xs font-medium">Group</span>
              <Select
                value={groupId || null}
                onValueChange={(next) => {
                  if (typeof next !== 'string') return
                  setValue('groupId', next)
                }}
                items={Object.fromEntries(editableGroups.map((group) => [group.groupId, group.name]))}
                disabled={pending || groupsQuery.isLoading}
              >
                <SelectTrigger aria-label="Group"><SelectValue placeholder={groupsQuery.isLoading ? 'Loading groups…' : 'Select a group…'} /></SelectTrigger>
                <SelectContent>
                  {editableGroups.map((group) => <SelectItem key={group.groupId} value={group.groupId}>{group.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {editableGroups.length === 0 ? <p className="text-muted-foreground text-xs">No locally editable groups are available here.</p> : null}
            </div>
          )}

          {alreadyHasRole ? <p role="alert" className="text-destructive text-xs">{user.name} already has this role directly at {selectedEntity.entityName}.</p> : null}
        </div>
      ) : null}

      {step === 'review' ? (
        <div className="flex flex-col gap-3">
          <div className="border-border rounded-md border p-4">
            <p className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">Access change</p>
            <p className="mt-2 text-sm font-medium">{user.name}</p>
            <p className="text-muted-foreground text-xs">{user.email}</p>
            <dl className="mt-4 grid grid-cols-[5rem_1fr] gap-x-3 gap-y-2 border-t pt-3 text-xs">
              <dt className="text-muted-foreground">Change</dt><dd>{accessType === 'role' ? 'Grant role' : 'Add to group'}</dd>
              {accessType === 'role' ? (
                <><dt className="text-muted-foreground">Entity</dt><dd>{selectedEntity.entityName}</dd><dt className="text-muted-foreground">Role</dt><dd>{selectedRole?.name}</dd></>
              ) : (
                <><dt className="text-muted-foreground">Group</dt><dd>{selectedGroup?.name}</dd></>
              )}
            </dl>
          </div>
          <p className="text-muted-foreground text-xs">This change takes effect immediately.</p>
        </div>
      ) : null}

      {assignRole.isError || addMember.isError ? (
        <p id={requestErrorId} role="alert" className="text-destructive text-xs">
          {assignRole.isError ? assignUserRoleErrorMessage(assignRole.error) : 'Couldn’t add the user to the group. Try again.'}
        </p>
      ) : null}

      <DialogFooter>
        <Button type="button" variant="outline" size="sm" disabled={pending} onClick={step === 'type' || (rolesOnly && step === 'details') ? onClose : () => setStep(step === 'review' ? 'details' : 'type')}>
          {step === 'type' || (rolesOnly && step === 'details') ? 'Cancel' : 'Back'}
        </Button>
        {step === 'type' ? (
          <Button type="button" size="sm" onClick={() => setStep('details')}>Continue</Button>
        ) : step === 'details' ? (
          <Button type="button" size="sm" disabled={!canReview || profileQuery.isLoading} onClick={() => setStep('review')}>Review change</Button>
        ) : (
          <Button
            type="button"
            size="sm"
            disabled={pending || !canReview}
            aria-describedby={assignRole.isError || addMember.isError ? requestErrorId : undefined}
            onClick={() => void handleSubmit(submitGrant)()}
          >
            {pending ? 'Granting…' : 'Grant access'}
          </Button>
        )}
      </DialogFooter>

    </form>
  )
}

export function AssignAccessDialog({
  entityId,
  entityName,
  user,
  open,
  onOpenChange,
  rolesOnly = false,
  serviceAccount = false,
}: {
  entityId: string
  entityName: string
  user: UserCandidate
  open: boolean
  onOpenChange: (open: boolean) => void
  rolesOnly?: boolean
  serviceAccount?: boolean
}) {
  const [pending, setPending] = useState(false)
  const announce = useAnnounce()

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!pending) onOpenChange(next) }}>
      <DialogContent className="max-w-lg">
        <DialogTitle>Grant access to {user.name}</DialogTitle>
        <DialogDescription>
          {rolesOnly
            ? `Grant a role to ${user.name} at an entity managed by ${entityName}.`
            : `Grant a role or add ${user.name} to a group managed by ${entityName}.`}
        </DialogDescription>
        {open ? (
          <AssignAccessPanel entityId={entityId} entityName={entityName} user={user} rolesOnly={rolesOnly} serviceAccount={serviceAccount} onClose={() => onOpenChange(false)} onPendingChange={setPending} onAssigned={announce} />
        ) : null}
      </DialogContent>
    </Dialog>
  )
}
